import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
// Goals page — work + personal goals as first-class entities.
//
// Two sections (Work / Personal) of goal cards, each card showing the
// "why this matters" subtitle, a progress bar (counter-vs-target),
// status pill (Active / Ahead / On track / At risk / Achieved), and a
// small toolbar for editing or marking achieved/abandoned. A header
// summary tile per category averages progress across that category's
// active goals so the user can see balance at a glance.
import { notify } from "../components/Toast";
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api/client";
import { useAuth } from "../hooks/useAuth";
import { useOrgCatalog } from "../hooks/useOrgCatalog";
import AlignmentTab from "../components/okr/AlignmentTab";
import CompanyOkrTab from "../components/okr/CompanyOkrTab";
import RoadmapTab from "../components/okr/RoadmapTab";
// ─── helpers ────────────────────────────────────────────────────────────
// Percentage complete for a single goal — clamped 0..100. All three goal
// types share the same math now: current_value / target_value, with a
// sensible fallback for each:
//   - percentage : default target_value = 100 if unset
//   - habit      : default target_value = 30 (one-month streak)
//   - counter    : returns 0 when target_value is missing (we can't infer one)
// Previously the percentage branch ignored target_value entirely, so a
// goal with target 80% and current 80% showed 80% instead of "100% to
// target." Fixed.
function clampPct(frac) {
    return Math.min(100, Math.max(0, Math.round(frac * 100)));
}
function resolvedUnits(g) {
    if (g.target_type === "habit")
        return null;
    if (g.target_type === "percentage") {
        // % growth goal — baseline + growth-% give a real-unit target.
        if (g.baseline_value != null &&
            g.target_value != null &&
            g.target_value > 0) {
            const base = g.baseline_value;
            const tgt = base * (1 + g.target_value / 100);
            const span = tgt - base;
            if (span <= 0)
                return null;
            return { base, cur: g.current_value, tgt, span };
        }
        // Legacy 0-100 % goal — target defaults to 100, base 0.
        const tgt = g.target_value && g.target_value > 0 ? g.target_value : 100;
        return { base: 0, cur: g.current_value, tgt, span: tgt };
    }
    // counter
    const tgt = g.target_value ?? 0;
    if (!tgt || tgt <= 0)
        return null;
    return { base: 0, cur: g.current_value, tgt, span: tgt };
}
function progressPct(g) {
    if (g.target_type === "habit") {
        const target = g.target_value && g.target_value > 0 ? g.target_value : 30;
        return clampPct(g.current_value / target);
    }
    const r = resolvedUnits(g);
    if (!r)
        return 0;
    return clampPct((r.cur - r.base) / r.span);
}
// Period-aware start of a goal's window. Returns the date we should
// treat as t=0 for pace math. Two branches:
//
// 1) BU-scoped goals carry an explicit period (monthly/quarterly/
//    yearly). The window is the period's natural boundary regardless
//    of when the goal was created. A yearly goal set up in March
//    still measures "elapsed" from Jan 1 — that's the only way the
//    user gets honest pace math against an annual target.
//
//    monthly   → first of target_date's month
//    quarterly → first day of the quarter containing target_date
//    yearly    → Jan 1 of target_date's year
//
// 2) Personal goals have no period. Use the goal's created_at as the
//    window start — the user picked the deadline and the count from
//    that date forward, so "elapsed since I committed" is the right
//    denominator. Returning target_date - 30 days here was wrong:
//    for a "1 chapter a day for the year" personal goal with no
//    period, that put the start in early December and made every
//    short-of-target value read as on-track.
//
// Returns null only when there's no target_date at all — then pace
// can't be computed.
function periodStartFor(g) {
    if (!g.target_date)
        return null;
    const [y, m, d] = g.target_date.split("-").map(Number);
    const target = new Date(y, (m || 1) - 1, d || 1);
    // Explicit start_date wins over every heuristic — the user told
    // us when the window opens, so trust it. Migration 0039 added
    // the column; existing rows return null and fall through to the
    // period-aware fallback below.
    if (g.start_date) {
        const [sy, sm, sd] = g.start_date.split("-").map(Number);
        return new Date(sy, (sm || 1) - 1, sd || 1);
    }
    if (g.period === "monthly") {
        return new Date(target.getFullYear(), target.getMonth(), 1);
    }
    if (g.period === "quarterly") {
        const qFirstMonth = Math.floor(target.getMonth() / 3) * 3;
        return new Date(target.getFullYear(), qFirstMonth, 1);
    }
    if (g.period === "yearly") {
        return new Date(target.getFullYear(), 0, 1);
    }
    // Personal / no-period — anchor to the goal's creation date so the
    // window is "from when you committed to this through the deadline".
    return new Date(g.created_at);
}
// Fraction of the goal's time window that has elapsed (0..1). Returns
// null when there's no target_date — without a deadline, "elapsed" is
// meaningless and pace can't be computed.
function elapsedFrac(g) {
    if (!g.target_date)
        return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const [y, m, d] = g.target_date.split("-").map(Number);
    const target = new Date(y, (m || 1) - 1, d || 1);
    const start = periodStartFor(g) ?? new Date(g.created_at);
    const total = Math.max(1, target.getTime() - start.getTime());
    const elapsed = today.getTime() - start.getTime();
    return Math.max(0, Math.min(1, elapsed / total));
}
// Pace = where you are vs where time says you should be. Returns a signed
// delta in the goal's units. Positive = ahead, negative = behind.
// Null when there's no target_date or no target_value (nothing to compare).
function paceDelta(g) {
    const ef = elapsedFrac(g);
    if (ef === null)
        return null;
    // Streak pace is conveyed by the flame row, not a unit delta.
    if (g.target_type === "habit")
        return null;
    const r = resolvedUnits(g);
    if (!r)
        return null;
    const expected = r.base + ef * r.span;
    return Math.round((r.cur - expected) * 100) / 100;
}
function healthFor(g) {
    if (g.status === "achieved")
        return "on_track";
    // Streak goals: alive = on track, broken = behind. A snapped streak
    // (current_value back to 0) is the only "behind" signal that matters.
    if (g.target_type === "habit") {
        return g.current_value > 0 ? "on_track" : "behind";
    }
    const ef = elapsedFrac(g);
    if (ef === null)
        return "on_track";
    const r = resolvedUnits(g);
    if (!r)
        return "on_track";
    const expected = r.base + ef * r.span;
    const tol = r.span * 0.10;
    if (r.cur >= expected + tol)
        return "ahead";
    if (r.cur >= expected - tol)
        return "on_track";
    // Behind. Distinguish behind vs at_risk by the catch-up ratio:
    // how much faster the user has to go vs how fast they've been
    // going. >3× is "at risk" territory.
    if (!g.target_date)
        return "behind";
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const [y, m, d] = g.target_date.split("-").map(Number);
    const end = new Date(y, (m || 1) - 1, d || 1);
    const daysLeft = Math.max(0, Math.round((end.getTime() - today.getTime()) / 86400000));
    if (daysLeft <= 0)
        return "at_risk";
    // Days the user has had to make progress, anchored to the
    // period's natural start (Jan 1 / quarter-first / month-first /
    // 30-days-before-deadline) — same as the backend's pace math. NOT
    // created_at, which would shrink the window for any goal that
    // wasn't set on day one of the period.
    const periodStart = periodStartFor(g) ?? new Date(g.created_at);
    const elapsedDays = Math.max(1, Math.round((today.getTime() - periodStart.getTime()) / 86400000));
    const currentPerDay = (r.cur - r.base) / elapsedDays;
    const neededPerDay = (r.tgt - r.cur) / daysLeft;
    if (currentPerDay <= 0)
        return "at_risk";
    return neededPerDay / currentPerDay > 3 ? "at_risk" : "behind";
}
// Boolean form of healthFor — used for the "N of M on track" headline
// metric. Behind + at_risk both count as not on track; ahead and
// on_track are both "keeping up with the plan".
function isOnTrack(g) {
    const h = healthFor(g);
    return h === "ahead" || h === "on_track";
}
function fmtTargetDate(iso) {
    if (!iso)
        return "No deadline";
    const [y, m, d] = iso.split("-").map(Number);
    const date = new Date(y, (m || 1) - 1, d || 1);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const ms = date.getTime() - today.getTime();
    const days = Math.round(ms / 86400000);
    if (days === 0)
        return "Today";
    if (days < 0)
        return `${-days} day${days === -1 ? "" : "s"} overdue`;
    if (days < 60)
        return `${days} day${days === 1 ? "" : "s"} left`;
    return date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
}
// "218 of 500 miles" / "3 of 24 books" / "60%" / "60% / 100%".
function fmtProgressDetail(g) {
    const rounded = (n) => Math.round(n * 10) / 10;
    if (g.target_type === "percentage") {
        // % growth goal with a baseline → read in real units ("5 / 9 producers").
        if (g.baseline_value != null && g.target_value != null && g.target_value > 0) {
            const r = resolvedUnits(g);
            if (r) {
                const unit = g.unit?.trim() || "units";
                return `${rounded(r.cur)} / ${rounded(r.tgt)} ${unit}`.trim();
            }
        }
        // Percentage goals with a target above 100 (and no baseline) are almost
        // certainly a user error — they probably meant counter. Display as a
        // counter-style ratio so the user sees the mistake and can flip the type.
        if (g.target_value && g.target_value > 100) {
            const unit = g.unit?.trim() || "(percentage target — looks like a counter?)";
            return `${rounded(g.current_value)} of ${rounded(g.target_value)} ${unit}`.trim();
        }
        return `${rounded(g.current_value)}%${g.target_value && g.target_value !== 100 ? ` / ${rounded(g.target_value)}%` : ""}`;
    }
    if (g.target_type === "habit") {
        const target = g.target_value ?? 30;
        return `${rounded(g.current_value)}-day streak · target ${rounded(target)}`;
    }
    // counter
    const tgt = g.target_value ?? 0;
    if (!tgt)
        return `${rounded(g.current_value)} ${g.unit ?? ""}`.trim();
    return `${rounded(g.current_value)} of ${rounded(tgt)} ${g.unit ?? ""}`.trim();
}
// "+2 of +6" growth-delta tag for the bignum's right slot on % growth
// goals (gained-so-far of the increase the goal demands). null otherwise.
function growthDelta(g) {
    if (g.target_type !== "percentage")
        return null;
    if (g.baseline_value == null || g.target_value == null || g.target_value <= 0) {
        return null;
    }
    const r = resolvedUnits(g);
    if (!r)
        return null;
    const rounded = (n) => Math.round(n * 10) / 10;
    return `+${rounded(r.cur - r.base)} of +${rounded(r.span)}`;
}
// Per-category headline: "N of M on track" — beats avg-percent because
// it surfaces the actually-meaningful question ("am I keeping up with
// my goals as a portfolio?") instead of letting one big number dominate.
function categoryProgress(goals, category) {
    const inCategory = goals.filter((g) => g.category === category);
    const active = inCategory.filter((g) => g.status === "active");
    const achieved = inCategory.filter((g) => g.status === "achieved").length;
    const onTrack = active.filter(isOnTrack).length;
    return { active, onTrack, achieved };
}
// Whole-unit goals (events, producers, books, people) should never display
// fractional counts. A goal is whole-unit when its target / current /
// baseline are all integers. Miles-style goals (137.5) keep one decimal.
function isWholeUnit(g) {
    return [g.target_value, g.current_value, g.baseline_value]
        .filter((v) => v != null)
        .every((v) => Number.isInteger(v));
}
// Format a derived count for display: whole for whole-unit goals, else 1 dp.
function fmtCount(g, n) {
    return isWholeUnit(g) ? Math.round(n) : Math.round(n * 10) / 10;
}
// Compact pace chip in real units ("3 producers behind pace" / "+1 ahead").
// null for streaks + goals with no pace signal.
function paceChip(g) {
    const pd = paceDelta(g);
    if (pd === null)
        return null;
    const unit = g.unit?.trim();
    const suffix = unit ? ` ${unit}` : "";
    const abs = fmtCount(g, Math.abs(pd));
    if (abs <= 0)
        return { text: "On pace", cls: "ok" };
    if (pd > 0)
        return { text: `+${abs}${suffix} ahead of pace`, cls: "ok" };
    return { text: `${abs}${suffix} behind pace`, cls: "bad" };
}
// Smart "how much per period" cadence. Picks week vs month so the number
// reads as a sensible whole figure instead of "0.3 / week": when the weekly
// rate falls below ~1 we express it per month instead. Whole-unit goals
// round to whole counts. null when there's no deadline / nothing left / streak.
function cadenceNeeded(g) {
    const r = resolvedUnits(g);
    if (!r || !g.target_date)
        return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const [y, m, d] = g.target_date.split("-").map(Number);
    const end = new Date(y, (m || 1) - 1, d || 1);
    const daysLeft = Math.round((end.getTime() - today.getTime()) / 86400000);
    if (daysLeft <= 0)
        return null;
    const remaining = r.tgt - r.cur;
    if (remaining <= 0)
        return null;
    const perWeek = remaining / Math.max(1, daysLeft / 7);
    const whole = isWholeUnit(g);
    // Whole-unit goals round UP — "need 2/month" never understates a 1.3/month
    // requirement down to a misleading "1". Fractional units keep one decimal.
    if (perWeek >= 0.95) {
        return {
            value: whole ? Math.max(1, Math.ceil(perWeek)) : Math.round(perWeek * 10) / 10,
            unit: "week",
        };
    }
    // Sub-1 per week → express monthly so it reads as whole events/month.
    const perMonth = remaining / Math.max(1, daysLeft / 30.4);
    return {
        value: whole ? Math.max(1, Math.ceil(perMonth)) : Math.round(perMonth * 10) / 10,
        unit: "month",
    };
}
// Projected finish date if the user holds their pace-so-far. "Jan 18" or
// null (no deadline, done, or no forward momentum to extrapolate).
function finishProjection(g) {
    const r = resolvedUnits(g);
    if (!r || r.cur >= r.tgt)
        return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const periodStart = periodStartFor(g) ?? new Date(g.created_at);
    const elapsedDays = Math.max(1, Math.round((today.getTime() - periodStart.getTime()) / 86400000));
    const perDay = (r.cur - r.base) / elapsedDays;
    if (perDay <= 0)
        return null;
    const daysToFinish = (r.tgt - r.cur) / perDay;
    if (!isFinite(daysToFinish) || daysToFinish > 3650)
        return null;
    const finish = new Date(today.getTime() + daysToFinish * 86400000);
    return finish.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
// Next quarter milestone (¼ / ½ / ¾ / goal) + units remaining to reach it.
// Drives the "🏅 50 mi to your ¼ milestone" badge. null for streaks / done.
function milestoneInfo(g) {
    const r = resolvedUnits(g);
    if (!r)
        return null;
    const frac = (r.cur - r.base) / r.span;
    const ticks = [
        [0.25, "¼"],
        [0.5, "½"],
        [0.75, "¾"],
        [1, "goal"],
    ];
    for (const [t, label] of ticks) {
        if (frac < t) {
            const remaining = fmtCount(g, r.base + t * r.span - r.cur);
            if (remaining <= 0)
                continue;
            return { label, remaining };
        }
    }
    return null;
}
// Streak badge ladder — the next milestone day-count above the current
// streak (7, 14, 30, 60, 100, 180, 365). null once past the top rung.
const STREAK_BADGES = [7, 14, 30, 60, 100, 180, 365];
function nextStreakBadge(streak) {
    for (const b of STREAK_BADGES)
        if (streak < b)
            return b;
    return null;
}
// Encouraging, concrete next-step line — concrete enough to act on, never
// scolding. Tailored to the goal's health band + type.
function nudgeFor(g) {
    if (g.status !== "active")
        return null;
    const health = healthFor(g);
    if (g.target_type === "habit") {
        const nb = nextStreakBadge(g.current_value);
        if (g.current_value <= 0) {
            return "🔁 Fresh start — log today and the chain begins again.";
        }
        if (nb) {
            const togo = nb - g.current_value;
            return `🔥 ${togo} more day${togo === 1 ? "" : "s"} to your ${nb}-day badge. Don't break the chain!`;
        }
        return "🔥 Incredible streak — keep the chain alive.";
    }
    const cad = cadenceNeeded(g);
    const unit = g.unit?.trim() ? ` ${g.unit.trim()}` : "";
    if (health === "ahead") {
        return cad
            ? `🚀 You're ahead — ${cad.value}${unit}/${cad.unit} keeps your lead.`
            : "🚀 You're ahead of pace — keep it rolling.";
    }
    if (health === "on_track") {
        return cad
            ? `✅ Right on pace — ${cad.value}${unit}/${cad.unit} holds the line.`
            : "✅ Right on pace — keep it up.";
    }
    // behind / at_risk → lead with the catch-up cadence.
    if (cad) {
        return health === "at_risk"
            ? `🎯 ${cad.value}${unit} per ${cad.unit} to still hit target — break it into wins.`
            : `📈 ${cad.value}${unit} per ${cad.unit} gets you back on track.`;
    }
    return null;
}
// Local YYYY-MM-DD (avoids the UTC shift toISOString() would introduce).
function isoDay(d) {
    return (d.getFullYear() +
        "-" +
        String(d.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(d.getDate()).padStart(2, "0"));
}
// First of the month containing `d`.
function firstOfMonth(d) {
    return new Date(d.getFullYear(), d.getMonth(), 1);
}
// Monday of the week containing `d` (the app is Mon-first throughout).
function mondayOf(d) {
    const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const dow = (x.getDay() + 6) % 7; // 0=Mon..6=Sun
    x.setDate(x.getDate() - dow);
    return x;
}
// Parse an ISO date (YYYY-MM-DD) into a local Date at midnight.
function parseIsoDay(iso) {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, (m || 1) - 1, d || 1);
}
// Build the ordered list of period rows from today's month/week through the
// goal's target_date (inclusive). Targets start blank — the caller pre-fills
// them from existing milestones or a split strategy. Returns [] when there's
// no target_date.
function buildPeriodRows(targetDate, period) {
    if (!targetDate)
        return [];
    const end = parseIsoDay(targetDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const rows = [];
    if (period === "month") {
        let cur = firstOfMonth(today);
        const endMonth = firstOfMonth(end);
        while (cur.getTime() <= endMonth.getTime() && rows.length < 60) {
            rows.push({
                periodStart: isoDay(cur),
                label: cur.toLocaleDateString(undefined, { month: "short" }),
                target: "",
            });
            cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
        }
    }
    else {
        let cur = mondayOf(today);
        const endWeek = mondayOf(end);
        while (cur.getTime() <= endWeek.getTime() && rows.length < 60) {
            rows.push({
                periodStart: isoDay(cur),
                label: "Wk " +
                    cur.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
                target: "",
            });
            cur = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + 7);
        }
    }
    return rows;
}
// How many whole weeks fall (at least partly) within a given month. Used to
// derive the "this week" sub-target from a monthly milestone target.
function weeksInMonth(monthStartIso) {
    const start = parseIsoDay(monthStartIso);
    const monthEnd = new Date(start.getFullYear(), start.getMonth() + 1, 0);
    const firstMon = mondayOf(start);
    let count = 0;
    let cur = new Date(firstMon);
    while (cur.getTime() <= monthEnd.getTime() && count < 6) {
        count += 1;
        cur = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + 7);
    }
    return Math.max(1, count);
}
// Spread `total` across `n` rows as integers that sum exactly to `total`.
// `weights` biases the distribution (front-load / ease-in); the rounding
// remainder is parceled out so the row sum always equals the goal.
function distribute(total, weights) {
    const n = weights.length;
    if (n === 0)
        return [];
    const sumW = weights.reduce((a, b) => a + b, 0) || n;
    // Raw (float) share per row, then floor + hand out the leftover units to
    // the rows with the largest fractional parts so the integer sum == total.
    const raw = weights.map((w) => (total * w) / sumW);
    const floors = raw.map((x) => Math.floor(x));
    let used = floors.reduce((a, b) => a + b, 0);
    let remainder = Math.round(total) - used;
    const order = raw
        .map((x, i) => ({ i, frac: x - Math.floor(x) }))
        .sort((a, b) => b.frac - a.frac);
    const out = [...floors];
    let k = 0;
    while (remainder > 0 && order.length > 0) {
        out[order[k % order.length].i] += 1;
        remainder -= 1;
        k += 1;
    }
    return out;
}
// The three split strategies. "remaining" is what's left to reach the goal
// (target_value - current_value, never negative). Even = equal weights;
// front-load = descending weights; ease-in = ascending weights.
function splitTargets(remaining, n, strategy) {
    if (n <= 0)
        return [];
    const total = Math.max(0, remaining);
    let weights;
    if (strategy === "front") {
        // n, n-1, … 1 — more up front, tapering down.
        weights = Array.from({ length: n }, (_, i) => n - i);
    }
    else if (strategy === "ease") {
        weights = Array.from({ length: n }, (_, i) => i + 1);
    }
    else {
        weights = Array.from({ length: n }, () => 1);
    }
    return distribute(total, weights);
}
// A goal "has a breakdown" when it carries a milestone period AND at least
// one milestone. Drives whether the card leads with the milestone surfaces
// (and suppresses the legacy auto-pace coaching) vs. the original layout.
function hasBreakdown(g) {
    return !!g.milestone_period && (g.milestones?.length ?? 0) > 0;
}
function milestoneStatus(goal) {
    if (!goal.milestone_period ||
        !goal.milestones ||
        goal.milestones.length === 0) {
        return null;
    }
    const ms = goal.milestones;
    const pastTarget = ms
        .filter((m) => m.is_past)
        .reduce((a, m) => a + m.target, 0);
    const pastActual = ms
        .filter((m) => m.is_past)
        .reduce((a, m) => a + m.actual, 0);
    const cur = ms.find((m) => m.is_current);
    let frac = 1;
    let curT = 0;
    let curA = 0;
    if (cur) {
        curT = cur.target;
        curA = cur.actual;
        const today = new Date();
        if (goal.milestone_period === "week") {
            const dow = (today.getDay() + 6) % 7; // Mon=0..Sun=6
            frac = (dow + 1) / 7; // elapsed fraction of the week
        }
        else {
            const dim = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
            frac = today.getDate() / dim; // elapsed fraction of the month
        }
    }
    const expected = pastTarget + curT * frac;
    const actual = pastActual + curA;
    if (expected <= 0)
        return "ontrack";
    if (actual >= expected * 1.05)
        return "ahead";
    if (actual < expected * 0.9)
        return "behind";
    return "ontrack";
}
// The pill label, CSS modifier, and hover title for a milestone status.
// `expected`/`actual` totals feed the explanatory tooltip.
function milestoneStatusPill(goal) {
    const st = milestoneStatus(goal);
    if (!st)
        return null;
    const ms = goal.milestones ?? [];
    const pastTarget = ms
        .filter((m) => m.is_past)
        .reduce((a, m) => a + m.target, 0);
    const pastActual = ms
        .filter((m) => m.is_past)
        .reduce((a, m) => a + m.actual, 0);
    const cur = ms.find((m) => m.is_current);
    let frac = 1;
    let curT = 0;
    let curA = 0;
    if (cur) {
        curT = cur.target;
        curA = cur.actual;
        const today = new Date();
        if (goal.milestone_period === "week") {
            const dow = (today.getDay() + 6) % 7;
            frac = (dow + 1) / 7;
        }
        else {
            const dim = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
            frac = today.getDate() / dim;
        }
    }
    const expected = pastTarget + curT * frac;
    const actual = pastActual + curA;
    const cadence = goal.milestone_period === "week" ? "weekly" : "monthly";
    const title = `Logged ${fmtCount(goal, actual)} of ~${fmtCount(goal, expected)} expected against your ${cadence} plan`;
    if (st === "ahead")
        return { label: "AHEAD", cls: "gs-ahead", title };
    if (st === "behind")
        return { label: "BEHIND PLAN", cls: "gs-behind", title };
    return { label: "ON TRACK", cls: "gs-ontrack", title };
}
// Title label for the "your target right now" band: the month name or
// "Wk of <Mon d>" for a milestone's period_start.
function milestoneLabel(periodStart, period) {
    const d = parseIsoDay(periodStart);
    if (period === "month") {
        return d.toLocaleDateString(undefined, { month: "long" });
    }
    return ("Wk of " + d.toLocaleDateString(undefined, { month: "short", day: "numeric" }));
}
// ─── Page ───────────────────────────────────────────────────────────────
export default function GoalsPage() {
    const { user } = useAuth();
    const [goals, setGoals] = useState([]);
    const { businessUnits: units } = useOrgCatalog();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [tab, setTab] = useState("all");
    const [editing, setEditing] = useState(null);
    // Only admin and managers can author BU monthly/quarterly goals. The
    // editor exposes those options conditionally based on this flag.
    const canManageBU = user?.role === "admin" || user?.role === "manager";
    // Owners can also see the company OKR rollup (they create/edit OKRs), even
    // though they're excluded from the BU-goal authoring gate above.
    const canSeeCompanyOkrs = canManageBU || user?.role === "owner";
    // Goal-specific UI (balance tiles, goal sections, "+ New goal") only shows
    // on the goal tabs; the OKR tabs render their own views below.
    const isGoalTab = tab === "all" || tab === "work" || tab === "personal";
    async function load() {
        setLoading(true);
        setError(null);
        try {
            const rows = await api.get("/me/goals");
            setGoals(rows);
        }
        catch (e) {
            setError(e instanceof Error ? e.message : "Couldn't load goals");
        }
        finally {
            setLoading(false);
        }
    }
    useEffect(() => {
        load();
    }, []);
    async function updateGoal(id, patch) {
        try {
            await api.patch(`/me/goals/${id}`, patch);
            await load();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't update goal");
        }
    }
    async function deleteGoal(id) {
        if (!confirm("Delete this goal? This can't be undone."))
            return;
        try {
            await api.del(`/me/goals/${id}`);
            await load();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't delete goal");
        }
    }
    const filtered = useMemo(() => {
        if (tab === "all")
            return goals;
        return goals.filter((g) => g.category === tab);
    }, [goals, tab]);
    const workActive = goals.filter((g) => g.category === "work" && g.status === "active");
    const personalActive = goals.filter((g) => g.category === "personal" && g.status === "active");
    if (!user)
        return null;
    // Work goals split into four buckets: personal/individual work goals
    // (period is null, owned by the caller), plus the three BU-wide
    // cadences (monthly / quarterly / yearly). Personal goals stay as a
    // single section.
    const workGoals = filtered.filter((g) => g.category === "work");
    const myWorkGoals = workGoals.filter((g) => !g.period);
    const buMonthlyGoals = workGoals.filter((g) => g.period === "monthly");
    const buQuarterlyGoals = workGoals.filter((g) => g.period === "quarterly");
    const buYearlyGoals = workGoals.filter((g) => g.period === "yearly");
    const personalGoals = filtered.filter((g) => g.category === "personal");
    return (_jsxs("div", { className: "page goals-page", children: [_jsxs("header", { className: "goals-head", children: [_jsxs("div", { children: [_jsxs("div", { className: "goals-eyebrow", children: ["Goals \u00B7 ", new Date().getFullYear()] }), _jsx("h2", { className: "goals-title", children: "Where you're headed" }), _jsxs("div", { className: "muted small", children: [workActive.length, " work \u00B7 ", personalActive.length, " personal"] })] }), _jsxs("div", { className: "goals-actions", children: [_jsxs("div", { className: "view-toggle", children: [_jsx("button", { className: tab === "all" ? "active" : "", onClick: () => setTab("all"), children: "All" }), _jsx("button", { className: tab === "work" ? "active" : "", onClick: () => setTab("work"), children: "Work" }), _jsx("button", { className: tab === "personal" ? "active" : "", onClick: () => setTab("personal"), children: "Personal" }), _jsx("button", { className: tab === "alignment" ? "active" : "", onClick: () => setTab("alignment"), children: "Alignment" }), canSeeCompanyOkrs && (_jsx("button", { className: tab === "okrs" ? "active" : "", onClick: () => setTab("okrs"), children: "Company OKRs" })), canSeeCompanyOkrs && (_jsx("button", { className: tab === "roadmap" ? "active" : "", onClick: () => setTab("roadmap"), children: "Roadmap" }))] }), isGoalTab && (_jsx("button", { className: "btn-primary", onClick: () => setEditing("new"), children: "+ New goal" }))] })] }), tab === "alignment" && _jsx(AlignmentTab, { user: user, units: units }), tab === "okrs" && _jsx(CompanyOkrTab, {}), tab === "roadmap" && _jsx(RoadmapTab, {}), isGoalTab && (_jsxs("div", { className: "goals-balance", children: [(() => {
                        const work = categoryProgress(goals, "work");
                        const fill = work.active.length === 0
                            ? 0
                            : Math.round((work.onTrack / work.active.length) * 100);
                        return (_jsxs("div", { className: "goals-balance-card goals-balance-work", children: [_jsx("div", { className: "balance-label", children: "Work goals" }), _jsxs("div", { className: "balance-value", children: [work.onTrack, " of ", work.active.length, _jsx("span", { className: "balance-sub", children: " on track" })] }), _jsx("div", { className: "balance-bar", children: _jsx("div", { className: "balance-bar-fill", style: { width: `${fill}%` } }) }), _jsxs("div", { className: "balance-meta muted small", children: [work.active.length, " active", work.achieved > 0 && ` · ${work.achieved} achieved`] })] }));
                    })(), (() => {
                        const personal = categoryProgress(goals, "personal");
                        const fill = personal.active.length === 0
                            ? 0
                            : Math.round((personal.onTrack / personal.active.length) * 100);
                        return (_jsxs("div", { className: "goals-balance-card goals-balance-personal", children: [_jsx("div", { className: "balance-label", children: "Personal goals" }), _jsxs("div", { className: "balance-value", children: [personal.onTrack, " of ", personal.active.length, _jsx("span", { className: "balance-sub", children: " on track" })] }), _jsx("div", { className: "balance-bar", children: _jsx("div", { className: "balance-bar-fill", style: { width: `${fill}%` } }) }), _jsxs("div", { className: "balance-meta muted small", children: [personal.active.length, " active", personal.achieved > 0 && ` · ${personal.achieved} achieved`] })] }));
                    })()] })), isGoalTab && loading && _jsx("div", { className: "muted", children: "Loading goals\u2026" }), isGoalTab && error && _jsx("div", { className: "error", children: error }), isGoalTab && !loading && goals.length === 0 && (_jsxs("div", { className: "goals-empty", children: [_jsx("p", { className: "muted", children: "No goals yet. Set one \u2014 a quarterly win at work, a habit you want to stick, a milestone you've been putting off." }), _jsx("button", { className: "btn-primary", onClick: () => setEditing("new"), children: "+ Add your first goal" })] })), (tab === "all" || tab === "work") && myWorkGoals.length > 0 && (_jsxs("section", { className: "goals-section", children: [_jsxs("div", { className: "goals-section-head", children: [_jsx("h3", { children: "My work goals" }), _jsx("span", { className: "muted small", children: "Personal commitments \u2014 only you see these." })] }), _jsx("div", { className: "goals-grid", children: myWorkGoals.map((g) => (_jsx(GoalCard, { goal: g, onEdit: () => setEditing(g), onDelete: () => deleteGoal(g.id), onPatch: (patch) => updateGoal(g.id, patch) }, g.id))) })] })), (tab === "all" || tab === "work") && buMonthlyGoals.length > 0 && (_jsxs("section", { className: "goals-section", children: [_jsxs("div", { className: "goals-section-head", children: [_jsx("h3", { children: "Business unit \u00B7 Monthly goals" }), _jsx("span", { className: "muted small", children: canManageBU
                                    ? "Set monthly commitments for your BU."
                                    : "Set by your admin / manager." })] }), _jsx("div", { className: "goals-grid", children: buMonthlyGoals.map((g) => (_jsx(GoalCard, { goal: g, onEdit: () => setEditing(g), onDelete: () => deleteGoal(g.id), onPatch: (patch) => updateGoal(g.id, patch) }, g.id))) })] })), (tab === "all" || tab === "work") && buQuarterlyGoals.length > 0 && (_jsxs("section", { className: "goals-section", children: [_jsxs("div", { className: "goals-section-head", children: [_jsx("h3", { children: "Business unit \u00B7 Quarterly goals" }), _jsx("span", { className: "muted small", children: canManageBU
                                    ? "Set quarterly commitments for your BU."
                                    : "Set by your admin / manager." })] }), _jsx("div", { className: "goals-grid", children: buQuarterlyGoals.map((g) => (_jsx(GoalCard, { goal: g, onEdit: () => setEditing(g), onDelete: () => deleteGoal(g.id), onPatch: (patch) => updateGoal(g.id, patch) }, g.id))) })] })), (tab === "all" || tab === "work") && buYearlyGoals.length > 0 && (_jsxs("section", { className: "goals-section", children: [_jsxs("div", { className: "goals-section-head", children: [_jsx("h3", { children: "Business unit \u00B7 Yearly goals" }), _jsx("span", { className: "muted small", children: canManageBU
                                    ? "Set yearly objectives for your BU."
                                    : "Set by your admin / manager." })] }), _jsx("div", { className: "goals-grid", children: buYearlyGoals.map((g) => (_jsx(GoalCard, { goal: g, onEdit: () => setEditing(g), onDelete: () => deleteGoal(g.id), onPatch: (patch) => updateGoal(g.id, patch) }, g.id))) })] })), (tab === "all" || tab === "personal") && personalGoals.length > 0 && (_jsxs("section", { className: "goals-section", children: [_jsx("div", { className: "goals-section-head", children: _jsx("h3", { children: "Personal" }) }), _jsx("div", { className: "goals-grid", children: personalGoals.map((g) => (_jsx(GoalCard, { goal: g, onEdit: () => setEditing(g), onDelete: () => deleteGoal(g.id), onPatch: (patch) => updateGoal(g.id, patch) }, g.id))) })] })), editing && (_jsx(GoalEditor, { initial: editing === "new" ? null : editing, defaultCategory: tab === "personal" ? "personal" : "work", units: units, canManageBU: canManageBU, onClose: () => setEditing(null), onSaved: async () => {
                    setEditing(null);
                    await load();
                } }))] }));
}
// ─── GoalCard ───────────────────────────────────────────────────────────
// Body for counter + percentage-growth goals: pace bar (milestone ticks +
// "where you should be" marker), insight chips, this-week target bar, an
// encouraging nudge, and the next-milestone badge.
function QuantityBody({ goal, pct, fillClass, breakdown, }) {
    const hasTarget = resolvedUnits(goal) !== null;
    const ef = hasTarget && !breakdown ? elapsedFrac(goal) : null;
    const pace = breakdown ? null : paceChip(goal);
    const cadence = cadenceNeeded(goal);
    const finish = breakdown ? null : finishProjection(goal);
    const milestone = breakdown ? null : milestoneInfo(goal);
    const nudge = breakdown ? null : nudgeFor(goal);
    const growth = growthDelta(goal);
    const unit = goal.unit?.trim() || "";
    const thisWeek = goal.this_week_value ?? 0;
    // The "this week" target bar only makes sense for weekly-cadence goals.
    // Low-frequency goals (cadence expressed per month) would show an ugly
    // fractional weekly target like "0 / 0.3", so we hide the bar for them.
    // Breakdown goals fold "this week" into the milestone spotlight, so hide
    // the legacy bar entirely for them.
    const weeklyTarget = !breakdown && cadence && cadence.unit === "week" ? cadence.value : null;
    return (_jsxs("div", { className: "goal-blend", children: [_jsxs("div", { className: "goal-bignum", children: [_jsx("span", { className: "gv", children: fmtProgressDetail(goal) }), _jsx("span", { className: "gpct", children: growth ?? `${pct}%` })] }), _jsxs("div", { className: "goal-blend-bar", children: [_jsx("i", { className: `gbar-fill ${fillClass}`, style: { width: `${pct}%` } }), hasTarget && !breakdown && (_jsxs(_Fragment, { children: [_jsx("span", { className: "gbar-tick", style: { left: "25%" } }), _jsx("span", { className: "gbar-tick", style: { left: "50%" } }), _jsx("span", { className: "gbar-tick", style: { left: "75%" } })] })), ef !== null && (_jsx("span", { className: "gbar-pace", style: { left: `${Math.round(ef * 100)}%` }, title: "Where you should be by now" }))] }), _jsxs("div", { className: "goal-chips", children: [pace && _jsx("span", { className: `gchip gchip-${pace.cls}`, children: pace.text }), !breakdown && cadence && (_jsxs("span", { className: "gchip gchip-warn", children: ["need ", cadence.value, unit ? ` ${unit}` : "", " / ", cadence.unit] })), finish && _jsxs("span", { className: "gchip", children: ["finish \u2248 ", finish] }), goal.target_date && (_jsxs("span", { className: "gchip", children: ["\u23F3 ", fmtTargetDate(goal.target_date)] }))] }), weeklyTarget != null && (_jsxs("div", { className: "goal-week", children: ["This week", _jsx("span", { className: "goal-wbar", children: _jsx("i", { style: {
                                width: `${Math.min(100, weeklyTarget > 0 ? (thisWeek / weeklyTarget) * 100 : 0)}%`,
                            } }) }), fmtCount(goal, thisWeek), " / ", weeklyTarget, unit ? ` ${unit}` : ""] })), nudge && _jsx("div", { className: "goal-nudge", children: nudge }), milestone && (_jsxs("div", { className: "goal-badge", children: ["\uD83C\uDFC5 ", milestone.remaining, unit ? ` ${unit}` : "", " to your", " ", milestone.label === "goal" ? "goal" : `${milestone.label} milestone`] }))] }));
}
// Body for habit (day-streak) goals: flame row, streak count, progress to
// streak target, next-badge + days-this-week chips, and a chain-keeping nudge.
function StreakBody({ goal, pct, fillClass, }) {
    const streak = Math.round(goal.current_value);
    const target = goal.target_value ?? 30;
    const lit = Math.max(0, Math.min(5, streak));
    const nextBadge = nextStreakBadge(streak);
    const thisWeek = Math.min(7, Math.round(goal.this_week_value ?? 0));
    const nudge = nudgeFor(goal);
    return (_jsxs("div", { className: "goal-blend", children: [_jsx("div", { className: "goal-flames", "aria-label": `${streak}-day streak`, children: [0, 1, 2, 3, 4].map((i) => (_jsx("span", { className: i < lit ? "" : "gflame-off", children: "\uD83D\uDD25" }, i))) }), _jsxs("div", { className: "goal-bignum", children: [_jsxs("span", { className: "gv", children: [streak, "-day streak"] }), _jsxs("span", { className: "gpct muted", children: [streak, " / ", Math.round(target), " days"] })] }), _jsx("div", { className: "goal-blend-bar", children: _jsx("i", { className: `gbar-fill ${fillClass}`, style: { width: `${pct}%` } }) }), _jsxs("div", { className: "goal-chips", children: [nextBadge && (_jsxs("span", { className: "gchip gchip-ok", children: ["next badge: ", nextBadge, "-day"] })), _jsxs("span", { className: "gchip", children: [thisWeek, " / 7 days this week"] })] }), nudge && _jsx("div", { className: "goal-nudge", children: nudge })] }));
}
// ─── Milestone spotlight + strip (goal card) ──────────────────────────────
// "Your target right now" band — the current period's actual vs target with a
// progress bar. For monthly breakdowns it also surfaces a derived "this week"
// sub-target (the month target split across the weeks in the month) so the
// week feels like the small, doable chunk. Celebratory, never scolding.
function MilestoneSpotlight({ goal }) {
    const period = (goal.milestone_period ?? "month");
    const milestones = goal.milestones ?? [];
    const current = milestones.find((m) => m.is_current);
    if (!current)
        return null;
    const unit = goal.unit?.trim() || "";
    const unitSuffix = unit ? ` ${unit}` : "";
    const tgt = current.target;
    const act = current.actual;
    const pct = tgt > 0 ? Math.min(100, Math.round((act / tgt) * 100)) : 0;
    const fillClass = pct >= 100 ? "gfill-ok" : "gfill-or";
    const label = milestoneLabel(current.period_start, period);
    // Derived weekly sub-target for a MONTHLY breakdown: the month target split
    // across the weeks in the current month. Uses the goal's existing
    // this_week_value vs that derived target so the week reads as "ahead 🔥"
    // when at/above pace.
    let weekLine = null;
    if (period === "month" && tgt > 0) {
        const nWeeks = weeksInMonth(current.period_start);
        const weeklyTarget = Math.max(1, Math.round(tgt / nWeeks));
        const thisWeek = goal.this_week_value ?? 0;
        const wkPct = weeklyTarget > 0
            ? Math.min(100, Math.round((thisWeek / weeklyTarget) * 100))
            : 0;
        const ahead = thisWeek >= weeklyTarget;
        weekLine = (_jsxs("div", { className: "ms-now-wk", children: [_jsxs("span", { className: `pill ${ahead ? "ms-pill-win" : "ms-pill-go"}`, children: [ahead ? "🔥 " : "", "This week ", fmtCount(goal, thisWeek), " / ", weeklyTarget, unitSuffix, ahead ? " · ahead" : ""] }), _jsx("span", { className: "ms-now-wkbar", "aria-hidden": "true", children: _jsx("i", { className: ahead ? "gfill-ok" : "gfill-or", style: { width: `${wkPct}%` } }) })] }));
    }
    return (_jsxs("div", { className: "ms-now", children: [_jsxs("div", { className: "ms-now-ey", children: ["Your target right now \u00B7 ", label] }), _jsxs("div", { className: "ms-now-big", children: [fmtCount(goal, act), " ", _jsxs("small", { children: ["/ ", fmtCount(goal, tgt), unitSuffix, " this ", period] })] }), _jsx("div", { className: "ms-now-bar", children: _jsx("i", { className: fillClass, style: { width: `${pct}%` } }) }), weekLine] }));
}
// Strip of small per-period cells. Past + hit → green check; past + short →
// amber (encouraging, not "failed"); current → highlighted "YOU'RE HERE";
// future → muted. Horizontally scrollable so a 12-month goal fits.
function MilestoneStrip({ goal }) {
    const period = (goal.milestone_period ?? "month");
    const milestones = goal.milestones ?? [];
    if (milestones.length === 0)
        return null;
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "ms-strip-h", children: [period === "month" ? "Monthly" : "Weekly", " milestones"] }), _jsx("div", { className: "ms-strip", children: milestones.map((m) => {
                    const hit = m.actual >= m.target && m.target > 0;
                    const pct = m.target > 0 ? Math.min(100, Math.round((m.actual / m.target) * 100)) : 0;
                    const cls = m.is_current
                        ? "ms-cell ms-cur"
                        : m.is_past
                            ? hit
                                ? "ms-cell ms-done"
                                : "ms-cell ms-miss"
                            : "ms-cell ms-fut";
                    const barClass = hit ? "gfill-ok" : "gfill-or";
                    const d = parseIsoDay(m.period_start);
                    const name = period === "month"
                        ? d.toLocaleDateString(undefined, { month: "short" })
                        : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
                    return (_jsxs("div", { className: cls, children: [_jsx("div", { className: "ms-cell-name", children: name }), _jsxs("div", { className: "ms-cell-val", children: [fmtCount(goal, m.actual), " / ", fmtCount(goal, m.target), hit ? " ✓" : ""] }), _jsx("div", { className: "ms-cell-bar", children: _jsx("i", { className: barClass, style: { width: `${pct}%` } }) }), m.is_current && _jsx("div", { className: "ms-youhere", children: "YOU'RE HERE" })] }, m.period_start));
                }) })] }));
}
function GoalCard({ goal, onEdit, onDelete, onPatch, }) {
    const pct = progressPct(goal);
    const health = healthFor(goal);
    const isAchieved = goal.status === "achieved";
    // A goal with a milestone breakdown leads with the milestone surfaces, and
    // its status is judged against the plan (milestoneStatusPill) rather than a
    // date-pace formula. Achieved/abandoned goals always keep their terminal
    // pill regardless of any breakdown.
    const breakdown = hasBreakdown(goal);
    const msPill = breakdown && goal.status === "active" ? milestoneStatusPill(goal) : null;
    const healthLabel = goal.status === "achieved"
        ? "Achieved"
        : goal.status === "abandoned"
            ? "Abandoned"
            : health === "ahead"
                ? "Ahead"
                : health === "at_risk"
                    ? "At risk"
                    : health === "behind"
                        ? "Behind"
                        : "On track";
    // Status-pill modifier matching the blended-card palette (ahead / ontrack
    // / behind / risk / done).
    const statusMod = goal.status === "achieved"
        ? "done"
        : goal.status === "abandoned"
            ? "muted"
            : health === "ahead"
                ? "ahead"
                : health === "at_risk"
                    ? "risk"
                    : health === "behind"
                        ? "behind"
                        : "ontrack";
    // Final pill: milestone-driven when the goal has a breakdown (and is
    // active), otherwise the existing health/status pill — unchanged for every
    // non-breakdown goal.
    const pillLabel = msPill ? msPill.label : healthLabel;
    const pillCls = msPill ? msPill.cls : `gs-${statusMod}`;
    const pillTitle = msPill ? msPill.title : undefined;
    // Bar fill colour follows health: green ahead, blue on-track, amber
    // behind, red at-risk. Streaks always read green (the chain is the win).
    const isHabit = goal.target_type === "habit";
    const fillClass = isHabit
        ? "gfill-ok"
        : goal.status === "achieved"
            ? "gfill-ok"
            : health === "ahead"
                ? "gfill-ok"
                : health === "on_track"
                    ? "gfill-blue"
                    : health === "at_risk"
                        ? "gfill-bad"
                        : health === "behind"
                            ? "gfill-warn"
                            : "gfill-blue";
    // Quick progress nudge — adds a sensible increment for the goal's
    // target_type so the user can log progress in one click.
    function bumpProgress() {
        let increment = 1;
        if (goal.target_type === "percentage")
            increment = 5;
        if (goal.target_type === "habit")
            increment = 1;
        onPatch({ current_value: goal.current_value + increment });
    }
    // BU-scoped goal cards get an extra meta line showing the BU + cadence
    // + who set them, plus a "company commitment" eyebrow so users see at
    // a glance that this isn't their personal goal.
    const isBuGoal = !!goal.period;
    const canEdit = goal.can_edit !== false; // legacy rows default to true
    return (_jsxs("article", { className: `goal-card goal-card-${goal.category} ${isAchieved ? "achieved" : ""} ${isBuGoal ? "goal-card-bu" : ""}`, children: [_jsxs("header", { className: "goal-card-head", children: [isBuGoal && (_jsxs("div", { className: "goal-card-eyebrow", children: [goal.period === "quarterly"
                                ? "QUARTERLY"
                                : goal.period === "yearly"
                                    ? "YEARLY"
                                    : "MONTHLY", goal.business_unit_name && (_jsxs(_Fragment, { children: [" \u00B7 ", goal.business_unit_name] })), goal.created_by_name && (_jsxs("span", { className: "muted small", children: [" ", "\u00B7 set by ", goal.created_by_name] }))] })), _jsxs("div", { className: "goal-card-title-row", children: [_jsx("h4", { className: "goal-card-title", children: goal.title }), _jsx("span", { className: `goal-status ${pillCls}`, title: pillTitle, children: pillLabel })] }), goal.why && _jsx("div", { className: "muted small goal-why", children: goal.why })] }), isHabit ? (_jsx(StreakBody, { goal: goal, pct: pct, fillClass: fillClass })) : (_jsx(QuantityBody, { goal: goal, pct: pct, health: health, fillClass: fillClass, breakdown: breakdown })), !isHabit && breakdown && (_jsxs(_Fragment, { children: [_jsx(MilestoneSpotlight, { goal: goal }), _jsx(MilestoneStrip, { goal: goal })] })), _jsx(GoalCardActions, { isHabit: isHabit, isAchieved: isAchieved, canEdit: canEdit, onLog: bumpProgress, onEdit: onEdit, onDelete: onDelete, onPatch: onPatch })] }));
}
// The single actions row for a goal card: an inline primary action plus a ⋯
// overflow menu for Edit / Mark achieved / Re-open / Delete. The menu closes
// on outside click or Escape.
function GoalCardActions({ isHabit, isAchieved, canEdit, onLog, onEdit, onDelete, onPatch, }) {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    useEffect(() => {
        if (!open)
            return;
        function onDoc(e) {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
            }
        }
        function onKey(e) {
            if (e.key === "Escape")
                setOpen(false);
        }
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);
    // The menu only carries can_edit-gated items, so for a worker on a BU goal
    // (no inline log when achieved, no menu items) we render nothing extra.
    const hasMenu = canEdit;
    return (_jsxs("div", { className: "goal-card-actions", children: [!isAchieved && (_jsx("button", { className: "btn-link", onClick: onLog, title: isHabit
                    ? "Check in for today"
                    : "Log a quick increment of progress", children: isHabit ? "✓ Check in today" : "+ Log progress" })), hasMenu && (_jsxs("div", { className: "goal-actions-more", ref: wrapRef, children: [_jsx("button", { type: "button", className: "goal-more-btn", "aria-label": "More actions", "aria-haspopup": "menu", "aria-expanded": open, onClick: () => setOpen((v) => !v), children: "\u22EF" }), open && (_jsxs("div", { className: "goal-more-menu", role: "menu", children: [_jsx("button", { type: "button", role: "menuitem", className: "goal-more-item", onClick: () => {
                                    setOpen(false);
                                    onEdit();
                                }, children: "Edit" }), !isAchieved && (_jsx("button", { type: "button", role: "menuitem", className: "goal-more-item", onClick: () => {
                                    setOpen(false);
                                    void onPatch({ status: "achieved" });
                                }, children: "Mark achieved" })), isAchieved && (_jsx("button", { type: "button", role: "menuitem", className: "goal-more-item", onClick: () => {
                                    setOpen(false);
                                    void onPatch({ status: "active" });
                                }, children: "Re-open" })), _jsx("button", { type: "button", role: "menuitem", className: "goal-more-item goal-more-danger", onClick: () => {
                                    setOpen(false);
                                    onDelete();
                                }, children: "Delete" })] }))] }))] }));
}
// ─── Milestone breakdown editor (inside GoalEditor) ───────────────────────
// "Break into milestones" — Monthly/Weekly toggle, one editable row per
// period from now through the goal's target_date, suggestion buttons, a live
// roll-up tally, and its own Save. Only shown for an existing counter/%
// goal that has both a target_value and a target_date. Saves via
// PUT /me/goals/{id}/milestones and refreshes the list on success.
function MilestoneSection({ goal, onSaved, }) {
    const target = goal.target_value ?? 0;
    const unit = goal.unit?.trim() || "";
    const unitSuffix = unit ? ` ${unit}` : "";
    const remaining = Math.max(0, target - goal.current_value);
    const [period, setPeriod] = useState(goal.milestone_period || "month");
    const [rows, setRows] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [saved, setSaved] = useState(false);
    // Rebuild the period rows whenever the toggle (or goal) changes, pre-filling
    // each row's target from the goal's existing milestones where they line up.
    useEffect(() => {
        const fresh = buildPeriodRows(goal.target_date ?? null, period);
        const existing = new Map();
        if (goal.milestone_period === period) {
            for (const m of goal.milestones ?? []) {
                existing.set(m.period_start, m.target);
            }
        }
        setRows(fresh.map((r) => existing.has(r.periodStart)
            ? { ...r, target: String(existing.get(r.periodStart)) }
            : r));
        setSaved(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [period, goal.id]);
    function applySplit(strategy) {
        const vals = splitTargets(remaining, rows.length, strategy);
        setRows((prev) => prev.map((r, i) => ({ ...r, target: String(vals[i] ?? 0) })));
        setSaved(false);
    }
    function setRowTarget(i, val) {
        setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, target: val } : r)));
        setSaved(false);
    }
    const sum = rows.reduce((acc, r) => acc + (Number(r.target) || 0), 0);
    const matches = Math.abs(sum - target) < 0.5;
    const diff = Math.round((sum - target) * 10) / 10;
    const tallyText = matches
        ? `${fmtCount(goal, sum)} / ${fmtCount(goal, target)}${unitSuffix} ✓`
        : `${fmtCount(goal, sum)} / ${fmtCount(goal, target)}${unitSuffix} · ${diff < 0 ? `${fmtCount(goal, -diff)} short` : `${fmtCount(goal, diff)} over`}`;
    // Even-split preview label for the suggestion button.
    const evenPreview = rows.length > 0 ? fmtCount(goal, remaining / rows.length) : 0;
    async function saveBreakdown() {
        setSaving(true);
        setError(null);
        try {
            const milestones = rows
                .map((r) => ({
                period_start: r.periodStart,
                target: Number(r.target) || 0,
            }))
                .filter((m) => m.target > 0);
            await api.put(`/me/goals/${goal.id}/milestones`, {
                period,
                milestones,
            });
            setSaved(true);
            await onSaved();
        }
        catch (e) {
            setError(e instanceof Error ? e.message : "Couldn't save the breakdown.");
        }
        finally {
            setSaving(false);
        }
    }
    async function clearBreakdown() {
        setSaving(true);
        setError(null);
        try {
            await api.put(`/me/goals/${goal.id}/milestones`, {
                period: null,
                milestones: [],
            });
            setSaved(true);
            await onSaved();
        }
        catch (e) {
            setError(e instanceof Error ? e.message : "Couldn't clear the breakdown.");
        }
        finally {
            setSaving(false);
        }
    }
    const hasExisting = !!goal.milestone_period && (goal.milestones?.length ?? 0) > 0;
    return (_jsxs("div", { className: "ms-edit", children: [_jsxs("div", { className: "ms-edit-head", children: [_jsx("h4", { children: "Break into milestones" }), _jsxs("p", { className: "muted small", children: ["A big number is easier in ", period === "month" ? "monthly" : "weekly", " ", "chunks. Set each one \u2014 or let us split it."] })] }), _jsxs("div", { className: "ms-seg", role: "radiogroup", "aria-label": "Milestone period", children: [_jsx("button", { type: "button", role: "radio", "aria-checked": period === "month", className: period === "month" ? "on" : "", onClick: () => setPeriod("month"), children: "Monthly" }), _jsx("button", { type: "button", role: "radio", "aria-checked": period === "week", className: period === "week" ? "on" : "", onClick: () => setPeriod("week"), children: "Weekly" })] }), _jsxs("div", { className: "ms-suggest", children: [_jsxs("button", { type: "button", className: "ms-chip-btn", onClick: () => applySplit("even"), children: ["\u2728 Split evenly (\u2248", evenPreview, unit ? ` ${unit}` : "", " / ", period, ")"] }), _jsx("button", { type: "button", className: "ms-chip-btn", onClick: () => applySplit("front"), children: "\uD83D\uDCC8 Front-load" }), _jsx("button", { type: "button", className: "ms-chip-btn", onClick: () => applySplit("ease"), children: "\uD83D\uDCC9 Ease in" })] }), rows.length === 0 ? (_jsx("p", { className: "muted small", children: "No periods to break down \u2014 check the goal's target date." })) : (_jsx("div", { className: "ms-rows", children: rows.map((r, i) => (_jsxs("div", { className: "ms-mo", children: [_jsx("label", { children: r.label }), _jsxs("div", { className: "ms-mo-ipt", children: [_jsx("input", { type: "number", value: r.target, onChange: (e) => setRowTarget(i, e.target.value), placeholder: "0" }), unit && _jsx("span", { className: "muted small", children: unit })] })] }, r.periodStart))) })), _jsxs("div", { className: `ms-tally${matches ? "" : " warn"}`, children: [_jsx("span", { className: "muted", children: "Adds up to" }), _jsx("b", { children: tallyText })] }), !matches && (_jsx("p", { className: "muted small ms-tally-note", children: "That's fine \u2014 we never silently change your number. Save when it feels right." })), error && _jsx("div", { className: "error", children: error }), saved && !error && (_jsx("div", { className: "muted small ms-saved", children: "Breakdown saved \u2713" })), _jsxs("div", { className: "ms-edit-actions", children: [hasExisting && (_jsx("button", { type: "button", className: "btn-link btn-danger", onClick: clearBreakdown, disabled: saving, children: "Clear breakdown" })), _jsx("button", { type: "button", className: "btn-primary", onClick: saveBreakdown, disabled: saving || rows.length === 0, children: saving ? "Saving…" : "Save breakdown" })] })] }));
}
// ─── GoalEditor modal ───────────────────────────────────────────────────
function GoalEditor({ initial, defaultCategory, units, canManageBU, onClose, onSaved, }) {
    const isEdit = !!initial;
    const [title, setTitle] = useState(initial?.title ?? "");
    const [why, setWhy] = useState(initial?.why ?? "");
    const [description, setDescription] = useState(initial?.description ?? "");
    const [category, setCategory] = useState(initial?.category ?? defaultCategory);
    const [targetType, setTargetType] = useState(initial?.target_type ?? "counter");
    const [targetValue, setTargetValue] = useState(initial?.target_value != null ? String(initial.target_value) : "");
    const [currentValue, setCurrentValue] = useState(initial?.current_value != null ? String(initial.current_value) : "0");
    // % growth baseline — the starting unit count. Only shown / sent for
    // percentage goals so "grow by 200%" can read in real units.
    const [baselineValue, setBaselineValue] = useState(initial?.baseline_value != null ? String(initial.baseline_value) : "");
    const [unit, setUnit] = useState(initial?.unit ?? "");
    const [targetDate, setTargetDate] = useState(initial?.target_date ?? "");
    // Optional explicit pace window start. When set, the Today
    // GoalRow + Goals page anchor pace math here instead of guessing
    // from period kind / created_at. Empty string = unset, falls back
    // to the heuristic.
    const [startDate, setStartDate] = useState(initial?.start_date ?? "");
    const [status, setStatus] = useState(initial?.status ?? "active");
    // Period + BU — only meaningful for work goals. Empty string in
    // `period` means "personal/individual" (period IS NULL on backend).
    const [period, setPeriod] = useState(initial?.period ?? "");
    const [businessUnitId, setBusinessUnitId] = useState(initial?.business_unit_id ?? "");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    async function save() {
        if (!title.trim()) {
            setError("Title is required.");
            return;
        }
        // Client-side guard so the user sees the requirement before the
        // server bounces them: monthly/quarterly need a BU.
        if (period && !businessUnitId) {
            setError("Monthly, quarterly, and yearly goals must be tied to a business unit.");
            return;
        }
        // % growth goals need both numbers or the real-unit math can't compute
        // the target (this is what left "Grow by 200%" stuck on a 0-100 scale).
        if (targetType === "percentage" &&
            (!targetValue || Number(targetValue) <= 0 || !baselineValue)) {
            setError("For a % growth goal, enter the growth % (e.g. 200) and the starting count (e.g. 3).");
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const body = {
                title: title.trim(),
                why: why.trim() || null,
                description: description.trim() || null,
                category,
                target_type: targetType,
                target_value: targetValue ? Number(targetValue) : null,
                // For a % growth goal, an empty current count starts you at the
                // baseline (you're at your starting number, 0% of the way to target).
                current_value: currentValue
                    ? Number(currentValue)
                    : targetType === "percentage" && baselineValue
                        ? Number(baselineValue)
                        : 0,
                // Baseline only applies to % growth goals; null it out otherwise so
                // counter / habit rows don't carry a stray baseline.
                baseline_value: targetType === "percentage" && baselineValue
                    ? Number(baselineValue)
                    : null,
                unit: unit.trim() || null,
                target_date: targetDate || null,
                start_date: startDate || null,
                status,
            };
            // Only include period/BU when authoring a work goal — personal
            // goals can never be BU-scoped and the backend rejects mismatches.
            if (category === "work") {
                body.period = period || null;
                body.business_unit_id = period ? businessUnitId || null : null;
            }
            else {
                body.period = null;
                body.business_unit_id = null;
            }
            if (isEdit && initial) {
                await api.patch(`/me/goals/${initial.id}`, body);
            }
            else {
                await api.post("/me/goals", body);
            }
            await onSaved();
        }
        catch (e) {
            setError(e instanceof Error ? e.message : "Save failed");
        }
        finally {
            setSaving(false);
        }
    }
    return (_jsx("div", { className: "modal-backdrop", onClick: onClose, children: _jsxs("div", { className: "modal", onClick: (e) => e.stopPropagation(), children: [_jsx("h2", { children: isEdit ? "Edit goal" : "New goal" }), _jsxs("label", { children: ["Goal type", _jsxs("div", { className: "goal-category-toggle", role: "radiogroup", "aria-label": "Goal category", children: [_jsx("button", { type: "button", role: "radio", "aria-checked": category === "work", className: `goal-category-btn ${category === "work" ? "active" : ""}`, onClick: () => setCategory("work"), children: "Work" }), _jsx("button", { type: "button", role: "radio", "aria-checked": category === "personal", className: `goal-category-btn ${category === "personal" ? "active" : ""}`, onClick: () => setCategory("personal"), children: "Personal" })] })] }), _jsxs("label", { children: ["Title", _jsx("input", { value: title, onChange: (e) => setTitle(e.target.value), autoFocus: true, placeholder: "Run 500 miles \u00B7 Hit 50 enterprise customers \u00B7 Read 24 books" })] }), _jsxs("label", { children: ["Why this matters ", _jsx("span", { className: "muted small", children: "\u00B7 optional" }), _jsx("input", { value: why, onChange: (e) => setWhy(e.target.value), placeholder: "Train for the half marathon \u00B7 Unlock series-A \u00B7 Read more deeply" })] }), _jsxs("label", { children: ["Type", _jsxs("select", { value: targetType, onChange: (e) => setTargetType(e.target.value), children: [_jsx("option", { value: "counter", children: "Counter (e.g. 24 books)" }), _jsx("option", { value: "percentage", children: "% growth (e.g. grow by 200%)" }), _jsx("option", { value: "habit", children: "Habit streak (days)" })] })] }), category === "work" && canManageBU && (_jsxs("div", { className: "row", children: [_jsxs("label", { children: ["Period", _jsxs("select", { value: period, onChange: (e) => setPeriod(e.target.value), children: [_jsx("option", { value: "", children: "My work goal (just me)" }), _jsx("option", { value: "monthly", children: "BU monthly" }), _jsx("option", { value: "quarterly", children: "BU quarterly" }), _jsx("option", { value: "yearly", children: "BU yearly" })] })] }), _jsxs("label", { children: ["Business unit", _jsxs("select", { value: businessUnitId, onChange: (e) => setBusinessUnitId(e.target.value), disabled: !period, title: !period
                                        ? "Pick a period (monthly / quarterly / yearly) to tag a BU"
                                        : "Which BU this goal applies to", children: [_jsx("option", { value: "", children: period ? "— pick a BU —" : "— not BU-scoped —" }), units.map((u) => (_jsx("option", { value: u.id, children: u.name }, u.id)))] })] })] })), targetType === "percentage" ? (
                /* % growth goal — three purpose-built fields so it's unambiguous
                   where the growth % vs. the unit counts go. "Grow by 200%" with a
                   starting count of 3 → target 9; current count is the live number
                   you log against. */
                _jsxs(_Fragment, { children: [_jsxs("div", { className: "row", children: [_jsxs("label", { children: ["Growth target %", _jsx("input", { type: "number", value: targetValue, onChange: (e) => setTargetValue(e.target.value), placeholder: "200" })] }), _jsxs("label", { children: ["Starting count ", _jsx("span", { className: "muted small", children: "\u00B7 baseline" }), _jsx("input", { type: "number", value: baselineValue, onChange: (e) => setBaselineValue(e.target.value), placeholder: "3" })] }), _jsxs("label", { children: ["Current count", _jsx("input", { type: "number", value: currentValue, onChange: (e) => setCurrentValue(e.target.value), placeholder: "3" })] }), _jsxs("label", { children: ["Unit", _jsx("input", { value: unit, onChange: (e) => setUnit(e.target.value), placeholder: "producers \u00B7 writers \u00B7 clients" })] })] }), _jsx("div", { className: "goal-baseline-hint muted small", children: baselineValue && targetValue
                                ? (() => {
                                    const b = Number(baselineValue);
                                    const p = Number(targetValue);
                                    if (!b || !p)
                                        return "Enter a growth % and a starting count.";
                                    const tgt = Math.round(b * (1 + p / 100) * 10) / 10;
                                    const add = Math.round((tgt - b) * 10) / 10;
                                    const u = unit.trim() ? ` ${unit.trim()}` : "";
                                    return `${p}% of ${b} = +${add} → target ${tgt}${u}. Log progress by raising the current count.`;
                                })()
                                : "Enter the growth % (e.g. 200) and the starting count — we'll compute the real target." })] })) : (_jsxs("div", { className: "row", children: [_jsxs("label", { children: ["Target value", _jsx("input", { type: "number", value: targetValue, onChange: (e) => setTargetValue(e.target.value), placeholder: targetType === "habit" ? "30" : "24" })] }), _jsxs("label", { children: ["Current value", _jsx("input", { type: "number", value: currentValue, onChange: (e) => setCurrentValue(e.target.value) })] }), _jsxs("label", { children: ["Unit ", _jsx("span", { className: "muted small", children: "\u00B7 optional" }), _jsx("input", { value: unit, onChange: (e) => setUnit(e.target.value), placeholder: "books \u00B7 miles \u00B7 customers" })] })] })), _jsxs("div", { className: "row", children: [_jsxs("label", { children: ["Start date", " ", _jsx("span", { className: "muted small", children: "\u00B7 optional \u00B7 when pace counting begins" }), _jsx("input", { type: "date", value: startDate, onChange: (e) => setStartDate(e.target.value) })] }), _jsxs("label", { children: ["Target date ", _jsx("span", { className: "muted small", children: "\u00B7 optional" }), _jsx("input", { type: "date", value: targetDate, onChange: (e) => setTargetDate(e.target.value) })] }), isEdit && (_jsxs("label", { children: ["Status", _jsxs("select", { value: status, onChange: (e) => setStatus(e.target.value), children: [_jsx("option", { value: "active", children: "Active" }), _jsx("option", { value: "achieved", children: "Achieved" }), _jsx("option", { value: "abandoned", children: "Abandoned" })] })] }))] }), _jsxs("label", { children: ["Notes ", _jsx("span", { className: "muted small", children: "\u00B7 optional" }), _jsx("textarea", { value: description, onChange: (e) => setDescription(e.target.value), rows: 3, placeholder: "Anything else worth remembering about this goal." })] }), isEdit &&
                    initial &&
                    (initial.target_type === "counter" ||
                        initial.target_type === "percentage") &&
                    initial.target_value != null &&
                    initial.target_value > 0 &&
                    !!initial.target_date && (_jsx(MilestoneSection, { goal: initial, onSaved: onSaved })), error && _jsx("div", { className: "error", children: error }), _jsxs("div", { className: "modal-actions", children: [_jsx("button", { onClick: onClose, disabled: saving, children: "Cancel" }), _jsx("button", { className: "btn-primary", onClick: save, disabled: saving, children: saving ? "Saving…" : isEdit ? "Save changes" : "Create goal" })] })] }) }));
}
